import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import AuthModal from "../components/auth/AuthModal";
import { trackFeatureStarted } from "../analytics/analytics";
import { ANALYTICS_FEATURES } from "../analytics/analytics.constants";
import {
    getGuestPostQuotaTodayKey,
    hasUsedGuestPostsToday as checkHasUsedGuestPostsToday,
    readGuestPostQuota,
    writeGuestPostQuota,
    GUEST_POST_QUOTA_STORAGE_KEY,
} from "../utils/guestPostQuota";

const AuthModalContext = createContext(null);

const DEFAULT_TITLE = "Sign in to continue";
const DEFAULT_DESCRIPTION = "Log in or create an account to keep going.";

function getCurrentPath() {
    return `${window.location.pathname}${window.location.search}`;
}

export function AuthModalProvider({ children }) {
    const user = useSelector((state) => state.auth.user);
    const [modalState, setModalState] = useState({
        open: false,
        redirectPath: null,
        title: DEFAULT_TITLE,
        description: DEFAULT_DESCRIPTION,
        initialTab: "login",
    });
    const [guestQuota, setGuestQuota] = useState(() => readGuestPostQuota());

    const syncGuestQuotaFromStorage = useCallback(() => {
        const next = readGuestPostQuota();
        setGuestQuota(next);
        return next;
    }, []);

    const recordGuestPostUsage = useCallback((guestId) => {
        if (!guestId) return guestQuota;

        const next = writeGuestPostQuota({
            guestId,
            usedOn: getGuestPostQuotaTodayKey(),
        });
        setGuestQuota(next);
        return next;
    }, [guestQuota]);

    const closeAuthModal = useCallback(() => {
        setModalState((current) => ({ ...current, open: false }));
    }, []);

    const openAuthModal = useCallback((options = {}) => {
        const initialTab = options.initialTab ?? "login";
        if (initialTab === "profile" && !user) {
            return;
        }

        trackFeatureStarted(ANALYTICS_FEATURES.AUTH);

        setModalState({
            open: true,
            redirectPath: options.redirectPath ?? getCurrentPath(),
            title: options.title ?? DEFAULT_TITLE,
            description: options.description ?? DEFAULT_DESCRIPTION,
            initialTab,
        });
    }, [user]);

    useEffect(() => {
        if (!user && modalState.open && modalState.initialTab === "profile") {
            setModalState((current) => ({ ...current, open: false }));
        }
    }, [user, modalState.open, modalState.initialTab]);

    useEffect(() => {
        const onStorage = (event) => {
            if (event.key === GUEST_POST_QUOTA_STORAGE_KEY || event.key === null) {
                syncGuestQuotaFromStorage();
            }
        };

        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, [syncGuestQuotaFromStorage]);

    const guestId = guestQuota.guestId ?? null;
    const hasUsedGuestPostsToday = checkHasUsedGuestPostsToday(guestQuota);

    const value = useMemo(
        () => ({
            openAuthModal,
            closeAuthModal,
            isAuthModalOpen: modalState.open,
            guestId,
            hasUsedGuestPostsToday,
            recordGuestPostUsage,
            syncGuestQuotaFromStorage,
        }),
        [
            openAuthModal,
            closeAuthModal,
            modalState.open,
            guestId,
            hasUsedGuestPostsToday,
            recordGuestPostUsage,
            syncGuestQuotaFromStorage,
        ]
    );

    return (
        <AuthModalContext.Provider value={value}>
            {children}
            <AuthModal
                open={modalState.open}
                redirectPath={modalState.redirectPath}
                title={modalState.title}
                description={modalState.description}
                initialTab={modalState.initialTab}
                onComplete={closeAuthModal}
                onClose={closeAuthModal}
            />
        </AuthModalContext.Provider>
    );
}

export function useAuthModal() {
    const context = useContext(AuthModalContext);

    if (!context) {
        throw new Error("useAuthModal must be used within AuthModalProvider");
    }

    return context;
}

export const GUEST_AUTH_MODAL = {
    title: "Get started",
    description: "Log in or create an account to unlock campaigns",
};

export const ONBOARDING_AUTH_MODAL = {
    initialTab: "profile",
    title: "Set up your business",
    description: "A few quick steps to personalize your experience.",
};

export const REGISTER_BUSINESS_AUTH_MODAL = {
    initialTab: "register",
    title: "Register your business",
    description: "Create an account to unlock marketing trends tailored to your industry.",
};

export const GUEST_POSTS_LIMIT_AUTH_MODAL = {
    initialTab: "register",
    title: "Create more posts",
    description:
        "You've used today's free AI post generation. ",
};

export const GUEST_SAVE_CAMPAIGN_AUTH_MODAL = {
    initialTab: "register",
    title: "Sign in to save your campaign",
    description: "You can't save your campaign without logging in. Sign up or log in to continue.",
};

/** @deprecated Use GUEST_AUTH_MODAL */
export const START_TRIAL_AUTH_MODAL = GUEST_AUTH_MODAL;
